import type { Networkish } from "@/types";
import type { FixedNumber } from "@ethersproject/bignumber";
import type { Strategies } from "@/../config/swr.config";
import React, { useCallback, useMemo, useState } from "react";

import UiChainSelector from "@/components/Ui/Widget/ChainSelector/Wrapper";
import useTokenBalances, { type SupWeb3Balance } from "@/hooks/api/useTokenBalances";
import GridCellPinToken from "@/components/Grid/Cell/PinToken";
import GridCellUnsupported from "@/components/Grid/Cell/UnsupportedCell";
import UiPinnedToken from "@/components/Ui/PinnedToken";
import UiTag from "@/components/Ui/Tag";
import { twMerge } from "tailwind-merge";
import { useStoredData, useStoredDataDispatch } from "@/store/Context";
import dappConfig from "@/../config/dapp.config";
import { type Column, SupDataGrid, SupInput, SupIconButton, SupText, SupTooltip } from "@repo/core-ui";
import { arrayIntersect, formatCurrencyCompact } from "@/utils";
import { SupPool, SupSearch, SupRefresh } from "@repo/core-ui";
import { isAddressEqual, isAddress, type Address } from "viem";

interface Web3BalanceProps {
  hideDust?: boolean;
  clickHandler?: any;
  showSearch?: boolean;
  showChains?: boolean;
  showForceReload?: boolean;
  showPinnedTokens?: boolean;
  className?: string;
  strategy?: Strategies;
  superPoolChains?: Networkish[];
}

export default function Web3Balance({
  hideDust = false,
  clickHandler,
  showSearch = true,
  strategy = "on-mount",
  showChains = true,
  showPinnedTokens = true,
  showForceReload = false,
  className,
  superPoolChains,
}: Web3BalanceProps) {
  const [query, setQuery] = useState<string>("");

  const [forceReload, setForceReload] = useState<number>(0);

  const handleForceReload = useCallback(() => {
    setForceReload(Date.now());
  }, []);

  const { data: balance, isLoading: isBalanceLoading } = useTokenBalances(hideDust, strategy, forceReload > 0, forceReload);

  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const balanceChains = useMemo(() => {
    const chainList = balance?.filter(b => !!b.chainId).map(b => b.chainId);
    return (chainList ? [...new Set(chainList)] : []) as Networkish[];
  }, [balance]);

  const selectedChains = useMemo(() => {
    return arrayIntersect(storedData.tokenSelectorChains, balanceChains);
  }, [balanceChains, storedData.tokenSelectorChains]);

  const filterBalance = useMemo(() => {
    let results = balance ? balance.slice() : ([] as SupWeb3Balance[]);
    if (results.length === 0) {
      return results;
    }

    if (query.length > 1) {
      results = results.filter(singleBalance => {
        return isAddress(query) ? isAddressEqual(query as Address, singleBalance.address) : singleBalance.symbol.toLowerCase().includes(query.toLowerCase());
      });
    }

    if (showChains && selectedChains.length > 0 && selectedChains.length !== balanceChains.length) {
      results = results.filter(singleBalance => selectedChains.includes(singleBalance.chainId));
    }
    return results;
  }, [balance, query, showChains, selectedChains, balanceChains.length]);

  const columns: Column<SupWeb3Balance>[] = [
    {
      cellClassName: clickHandler && "cursor-pointer",
      field: "symbol",
      resizable: false,
      draggable: false,
      headerName: "Token",
      valueGetter: row => `${row.symbol} ${row.address}`,
      renderCell: row => (
        <GridCellUnsupported isUnsupported={row.isUnsupported}>
          <UiTag asset={row} chainId={row.chainId} />
        </GridCellUnsupported>
      ),
    },
    {
      cellClassName: clickHandler && "cursor-pointer",
      resizable: false,
      width: 90,
      draggable: false,
      field: "amountFixedNumber",
      type: "number",
      align: "right",
      headerName: "Balance",
      valueGetter: row => (row.amountFixedNumber ? (row.amountFixedNumber as FixedNumber).round(dappConfig.decimalsBigRoundingLimit).toUnsafeFloat() : 0),
      renderCell: row => {
        if (!row.amountFixedNumber) return;

        const amount = (row.amountFixedNumber as FixedNumber).round(dappConfig.decimalsBigRoundingLimit).toString();
        const isCompact = amount.length > 10;

        return (
          <SupTooltip placement="top" unmount portal content={row.amountFixedNumber ? (row.amountFixedNumber as FixedNumber).toString() : ""} color="primary">
            <span className={twMerge(row.isUnsupported && "opacity-44", "cursor-help text-[14px]")}>
              {isCompact ? formatCurrencyCompact(parseFloat(amount), 2, 0, "decimal") : amount}
            </span>
          </SupTooltip>
        );
      },
    },
    {
      cellClassName: clickHandler && "cursor-pointer",
      resizable: false,
      width: 75,
      maxWidth: 75,
      field: "amountInFiat",
      type: "number",
      align: "right",
      headerName: "USD",
      valueGetter: row => (row.amountInFiat ? parseFloat(row.amountInFiat as string) : 0),
      renderCell: row => (
        <span className={twMerge(row.isUnsupported && "opacity-44", "overflow-hidden text-[16px]")}>
          {formatCurrencyCompact(row.amountInFiat ? parseFloat(row.amountInFiat as string) : 0)}
        </span>
      ),
    },
    {
      resizable: false,
      draggable: false,
      width: 30,
      maxWidth: 30,
      field: "pin",
      sortable: false,
      align: "right",
      headerName: "",
      renderCell: row => <GridCellPinToken singleBalance={row} />,
    },
  ];

  const mappedTokenBalances = useMemo(() => {
    if (!balance) {
      return [];
    }
    return balance.filter(
      singleBalance => storedData.favTokens.find(favToken => favToken.tokenAddress === singleBalance.address && favToken.chainId == singleBalance.chainId) !== undefined
    ) as SupWeb3Balance[];
  }, [balance, storedData.favTokens]);

  const handleChainSelection = useCallback(
    (singleChain: Networkish) => {
      const preparedSelectedChains = selectedChains.includes(singleChain) ? selectedChains.filter(chain => chain != singleChain) : [...selectedChains, singleChain];
      storedDispatch({ type: "setTokenSelectorChains", tokenSelectorChains: preparedSelectedChains });
    },
    [selectedChains, storedDispatch]
  );

  const handleChainSelectAll = useCallback(() => {
    storedDispatch({ type: "setTokenSelectorChains", tokenSelectorChains: balanceChains });
  }, [balanceChains, storedDispatch]);

  const handleChainClearAll = useCallback(() => {
    storedDispatch({ type: "setTokenSelectorChains", tokenSelectorChains: [] });
  }, [storedDispatch]);

  const groupBySuperPool = useCallback((chainId: Networkish) => superPoolChains?.includes(chainId) ?? false, [superPoolChains]);
  const hasSuperPoolChains = useMemo(() => (superPoolChains?.length ?? 0) > 0, [superPoolChains]);

  const handleSuperPoolsRowClass = useCallback(
    (row: SupWeb3Balance | SupWeb3Balance[]) => {
      const { chainId } = row as SupWeb3Balance;
      if (!superPoolChains || superPoolChains.length < 1) {
        return "";
      }
      return superPoolChains.includes(chainId) ? "sup-rdg-superpools-row" : "";
    },
    [superPoolChains]
  );

  return (
    <div className={twMerge("flex h-full flex-col space-y-6 overflow-hidden", className)} id="balance-container">
      {(showSearch || showChains) && (
        <div>
          {showSearch && <SupInput clearable type="text" prefixIcon={<SupSearch />} value={query} fluid placeholder="SEARCH SYMBOL/ADDRESS" onChange={e => setQuery(e ?? "")} />}

          {showChains && (
            <UiChainSelector
              defaultExpanded
              onClear={handleChainClearAll}
              onSelectAll={handleChainSelectAll}
              groupLabel={
                hasSuperPoolChains ? (
                  <div className="inline-flex w-full flex-nowrap items-center space-x-1 text-[#5B49B8]">
                    <SupText variant="caption" weight="bold">
                      SuperPool Chains
                    </SupText>
                    <SupPool className="h-6 w-6 drop-shadow-md" />
                  </div>
                ) : undefined
              }
              label={hasSuperPoolChains ? "Other chains" : "Select chain(s)"}
              groupBy={hasSuperPoolChains ? groupBySuperPool : undefined}
              chainList={balanceChains}
              selectedChains={selectedChains}
              onClick={handleChainSelection}
            />
          )}
        </div>
      )}

      {showPinnedTokens && mappedTokenBalances.length > 0 && (
        <div className="space-y-2">
          <SupText variant="caption" weight="bold" as="h3">
            Pinned tokens
          </SupText>
          <ul className="flex flex-wrap items-center gap-[10px]">
            {mappedTokenBalances.map(pinnedBalance => (
              <UiPinnedToken
                isUnsupported={pinnedBalance.isUnsupported}
                key={`balance-token-${pinnedBalance.address}-${pinnedBalance.chainId}`}
                clickHandler={clickHandler}
                token={pinnedBalance}
              />
            ))}
          </ul>
        </div>
      )}

      {showForceReload && (
        <div className="flex items-center justify-between">
          <SupText variant="subtitle1" as="h3" className="text-neutral-900">
            Your tokens
          </SupText>
          <SupTooltip content="Refresh token balances" color="primary" unmount portal>
            <SupIconButton onClick={handleForceReload} active={isBalanceLoading} activeIcon={<SupRefresh className="h-6 w-6 animate-spin text-neutral-700" />}>
              <SupRefresh className="h-6 w-6 text-neutral-700 hover:text-neutral-700/80" />
            </SupIconButton>
          </SupTooltip>
        </div>
      )}

      <div className="flex-grow overflow-hidden">
        <SupDataGrid
          enableVirtualization
          loading={isBalanceLoading}
          className="custom-scrollbar h-full w-full overflow-x-hidden p-0"
          defaultSorting={[{ field: "amountInFiat", direction: "DESC" }]}
          onRowClick={row => {
            const { isUnsupported } = row as SupWeb3Balance;
            if (clickHandler && !isUnsupported) {
              clickHandler(row);
            }
          }}
          rowClass={handleSuperPoolsRowClass}
          data={filterBalance}
          rowId={row => `${row.address}_${row.chainId}`}
          columns={columns}
        />
      </div>
    </div>
  );
}
